import { useState } from 'react';
import { Summary } from '|/app/services';
import currency from '|/helpers/currency';
import { getStateNameFromAbbreviation } from '|/helpers/us-states';

const useOrderSummaryVm = (summary: Summary | null) => {
  const {
    customer: {
      firstName = null,
      middleName = null,
      lastName = null,
      suffix = null,
      address = null,
      address: {
        line1: customerLine1 = null,
        line2: customerLine2 = null,
        city: customerCity = null,
        state: customerState = null,
        zipCode: customerZipCode = null
      } = {},
      phoneNumber = null,
      emailAddress = null,
      isEvTaxCreditEligible: isCustomerEvTaxCreditEligible = null
    } = {},
    cobuyer: {
      firstName: cobuyerFirstName = null,
      middleName: cobuyerMiddleName = null,
      lastName: cobuyerLastName = null,
      suffix: cobuyerSuffix = null
    } = {},
    vehicle: {
      year: vehicleYear = null,
      make: vehicleMake = null,
      model: vehicleModel = null,
      engineDescription = null,
      odometer: vehicleOdometer = null,
      trim: vehicleTrim = null,
      inventoryType = null,
      primaryImageUrl = null,
      fallbackImageUrl = null,
      id: vehicleId = null,
      isEvTaxCreditEligible: isVehicleEvTaxCreditEligible = null
    } = {},
    tradeIn: {
      lienAmount = null,
      value: tradeInValue = null,
      year: tradeInYear = null,
      make: tradeInMake = null,
      model: tradeInModel = null,
      status: tradeInStatus = null
    } = {},
    product: {
      type: productType = null,
      price: productPrice = null,
      status: productStatus = null
    } = {},
    gap: { status: gapStatus = null, price: gapPrice = null } = {},
    payment: {
      creditFromPreviousOrder = null,
      dueAtOrder = null,
      cashDown = null,
      refundDue = null,
      isShippingFeeNonRefundable = null,
      paymentAccounts = []
    } = {},
    financing: {
      isEligibleForCoApplicant = null,
      isCoApplicantToggleOn = null
    } = {},
    funding: {
      type: fundingType = null,
      institution: fundingInstitution = null,
      thirdParty: {
        lenderName = null,
        breakdown: {
          cash: thirdPartyCash = null,
          loanAmount: thirdPartyLoanAmount = null
        } = {}
      } = {},
      loan: {
        term: loanTerm = null,
        payment: loanPayment = null,
        apr: loanApr = null,
        amountFinanced = null
      } = {}
    } = {},
    enrolledInAutopay = null,
    transfer: {
      deliveryType = null,
      address: transferAddress = null,
      serviceTimeZone = null,
      scheduledTimeSlot = null,
      windowStartTime = null,
      windowEndTime = null
    } = {},
    fees: feesList = [],
    totals: {
      totalPrice = null,
      vehicleStickerPrice = null,
      taxes = null,
      fees: totalFees = null,
      discounts: totalDiscounts = null,
      rebates: totalRebates = null,
      deliveryCharge = null,
      shippingFee = null,
      tradeInValue: totalTradeInValue = null,
      tradeInTaxSavings = null
    } = {},
    rebates = []
  } = summary || {};

  const [collapsedFees, setCollapsedFees] = useState(true);
  const [
    isPaymentBreakdownCollapsed,
    setIsPaymentBreakdownCollapsed
  ] = useState(true);

  const hasAddress = address != null;
  const customerName = [firstName, middleName, lastName, suffix]
    .filter(Boolean)
    .join(' ');
  const hasPii = customerName && phoneNumber && hasAddress;
  const cobuyerName = [
    cobuyerFirstName,
    cobuyerMiddleName,
    cobuyerLastName,
    cobuyerSuffix
  ]
    .filter(Boolean)
    .join(' ');
  const hasCoBuyerName = cobuyerName?.length > 1;
  const isCarvanaFinancing =
    fundingType === 'Financing' && fundingInstitution === 'Carvana';
  const isCash = fundingType?.toLowerCase() === 'cash';
  const isThirdParty = fundingType?.toLowerCase() === 'thirdparty';
  const showPaymentDetails =
    cashDown !== null ||
    creditFromPreviousOrder !== null ||
    shippingFee !== null ||
    thirdPartyCash !== null;
  const showDeliveryFee = deliveryCharge !== null && deliveryCharge > 0;
  const stateName =
    customerState !== null ? getStateNameFromAbbreviation(customerState) : '';
  const hasAncillaryProducts =
    (gapStatus !== null && gapStatus !== 'NotSet') ||
    (productStatus !== null && productStatus !== 'NotSet');
  const declinedAncillary = (productStatus || 'NotSet') === 'Declined';
  const tradeDeclined = tradeInStatus === 'Declined';
  const completedTrade = tradeInStatus !== null && tradeInStatus !== 'NotSet';
  const calculatedTradeValue = tradeInValue
    ? tradeInValue - (lienAmount ?? 0)
    : 0;
  const calculatedTotalRebates = totalRebates ?? 0;
  const tradeMessage =
    calculatedTradeValue >= 0
      ? `Your trade-in value of ${currency(
          calculatedTradeValue
        )} has been applied to your purchase as a down payment, reducing the total amount you need to finance.`
      : `Your current loan balance is higher than your car's value. This is known as "negative equity." The ${currency(
          calculatedTradeValue
        )} you still owe on your trade-in has been applied to your purchase.  Part of this balance has been added to your new loan and the remaining balance has been added to your down payment.`;
  const totalTaxes = taxes || 0;
  const totalFeesAmount = totalFees || 0;
  const totalDiscountsAmount = totalDiscounts || 0;
  const cashSubtotal = (totalPrice || 0) - (shippingFee || 0);
  const totalFinanced = isThirdParty
    ? thirdPartyLoanAmount || 0
    : amountFinanced || 0;
  const cashDownAmount =
    isThirdParty && (thirdPartyCash || 0) > 0
      ? thirdPartyCash || 0
      : cashDown || 0;

  const showTradeInEdit = completedTrade;
  const showProtectionEdit =
    productStatus !== null && productStatus !== 'NotSet';

  const hasTransferMethod =
    deliveryType?.toLowerCase()?.includes('pickup') ||
    deliveryType?.toLowerCase()?.includes('delivery');
  const isPickup =
    hasTransferMethod && deliveryType?.toLowerCase()?.includes('pickup');
  const isDelivery =
    hasTransferMethod && deliveryType?.toLowerCase()?.includes('delivery');
  const hasScheduledDate = scheduledTimeSlot
    ? !!scheduledTimeSlot
    : !!windowStartTime;
  const isMarketplaceVehicle = inventoryType === 'DealerMarketplace';
  const scheduledTime = scheduledTimeSlot || windowStartTime;
  const hasTimeWindows = windowStartTime !== null && windowEndTime !== null;
  const timezone =
    serviceTimeZone
      ?.split(' ')
      .map(x => x.charAt(0))
      .join('') || '';

  const hasPaymentMethodBeenSelected = fundingType !== null;
  const hasTerms = loanTerm !== null;
  const isOutsideFinance =
    fundingType?.toLowerCase() === 'financing' && !isCarvanaFinancing;
  const dueLabel = refundDue ? 'Refund due' : 'Due at order';
  const dueAmount = refundDue ? refundDue : dueAtOrder;
  const showEvTaxCredit =
    isCustomerEvTaxCreditEligible || isVehicleEvTaxCreditEligible;
  const hasEvTaxCredit = rebates?.some(rebate => rebate.name === 'EVTaxCredit');
  const evTaxCreditAmount = hasEvTaxCredit
    ? rebates?.find(rebate => rebate?.name === 'EVTaxCredit')?.amount
    : null;
  const hasSelectedEvTaxCreditOption = isCustomerEvTaxCreditEligible !== null;
  const downPaymentAccount = paymentAccounts.find(
    account => account.type === 'DownPayment'
  );
  const autopayAccount = paymentAccounts.find(
    account => account.type === 'Autopay'
  );

  return {
    hasAddress,
    customerName,
    hasPii,
    phoneNumber,
    emailAddress,
    customerLine1,
    customerLine2,
    customerCity,
    customerState,
    customerZipCode,
    isCoApplicantToggleOn,
    isEligibleForCoApplicant,
    cobuyerName,
    hasCoBuyerName,
    isCarvanaFinancing,
    isCash,
    isThirdParty,
    showPaymentDetails,
    showDeliveryFee,
    stateName,
    hasAncillaryProducts,
    declinedAncillary,
    collapsedFees,
    setCollapsedFees,
    tradeDeclined,
    completedTrade,
    calculatedTradeValue,
    calculatedTotalRebates,
    tradeMessage,
    totalTaxes,
    totalFeesAmount,
    totalDiscountsAmount,
    cashSubtotal,
    totalTradeInValue,
    totalFinanced,
    cashDownAmount,
    showTradeInEdit,
    showProtectionEdit,
    isPaymentBreakdownCollapsed,
    setIsPaymentBreakdownCollapsed,
    shippingFee,
    deliveryCharge,
    gapStatus,
    gapPrice,
    productStatus,
    productPrice,
    productType,
    totalPrice,
    tradeInStatus,
    tradeInYear,
    tradeInMake,
    tradeInModel,
    tradeInValue,
    lienAmount,
    dueAtOrder,
    cashDown,
    creditFromPreviousOrder,
    thirdPartyCash,
    vehicleStickerPrice,
    feesList,
    hasTransferMethod,
    isPickup,
    isDelivery,
    hasScheduledDate,
    vehicleYear,
    vehicleMake,
    vehicleModel,
    vehicleTrim,
    vehicleOdometer,
    engineDescription,
    isMarketplaceVehicle,
    scheduledTime,
    hasTimeWindows,
    windowStartTime,
    windowEndTime,
    timezone,
    address,
    hasPaymentMethodBeenSelected,
    hasTerms,
    isOutsideFinance,
    fundingInstitution,
    lenderName,
    amountFinanced,
    loanApr,
    loanTerm,
    loanPayment,
    thirdPartyLoanAmount,
    enrolledInAutopay,
    transferAddress,
    primaryImageUrl,
    fallbackImageUrl,
    dueLabel,
    refundDue,
    dueAmount,
    isShippingFeeNonRefundable,
    vehicleId,
    tradeInTaxSavings,
    hasEvTaxCredit,
    evTaxCreditAmount,
    showEvTaxCredit,
    isVehicleEvTaxCreditEligible,
    hasSelectedEvTaxCreditOption,
    downPaymentAccount,
    autopayAccount
  };
};

export default useOrderSummaryVm;
